import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';
import {
  BeneficiaryAgreedIncentiveOffer,
  IncentiveOffer,
} from 'modules/incentivesMarket/types';

import { IncentiveOffersListContextType } from '../types/IncentiveOffersListContextType';
import { incentiveOffersListInitValue } from '../constants/IncentiveOffersListInitValue';
import { IncentiveOfferSortOption } from '../constants/incentiveOffersSortOptions';
import {
  TRADING_OFFERS_PAGE_SIZE,
  TRADING_OFFERS_FILTERS,
} from '../../../constants/incentiveOffersConfig';
import { useGetIncentiveOffers } from '../../../hooks/useGetIncentiveOffers';
import calculateOffersSummary from '../../../utils/calculateOffersSummary';
import { filterSellOffers, filterBuyOffers } from '../../../utils/filterOffersByType';

export const IncentiveOffersListContext =
  createContext<IncentiveOffersListContextType>(incentiveOffersListInitValue);

export const IncentiveOffersListProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const { societyId, userId } = useParams<{
    societyId: string;
    userId: string;
  }>();
  const beneficiaryQuery = new URLSearchParams(location.search).get('beneficiaryId');
  const beneficiaryQueryParam = beneficiaryQuery ? `?beneficiaryId=${beneficiaryQuery}` : '';

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );
  const incentiveOffers = useSelector(
    ({ incentiveOffers }: State) => (incentiveOffers ?? []) as IncentiveOffer[]
  );

  const beneficiaryOperations = useMemo<BeneficiaryAgreedIncentiveOffer[]>(() => [], []);
  const [sellOffersSortBy, setSellOffersSortBy] =
    useState<IncentiveOfferSortOption>('price');
  const [buyOffersSortBy, setBuyOffersSortBy] =
    useState<IncentiveOfferSortOption>('price');

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { isLoading: isIncentiveOffersLoading } = useGetIncentiveOffers({
    societyId: actualSociety ? societyId : undefined,
    page: 1,
    rowsPerPage: TRADING_OFFERS_PAGE_SIZE,
    filters: TRADING_OFFERS_FILTERS,
  });

  const isReadOnly = false;

  const sellOffers = useMemo(
    () => filterSellOffers(incentiveOffers),
    [incentiveOffers]
  );

  const buyOffers = useMemo(
    () => filterBuyOffers(incentiveOffers),
    [incentiveOffers]
  );

  const sellOffersSummary = useMemo(
    () => calculateOffersSummary(sellOffers),
    [sellOffers]
  );

  const buyOffersSummary = useMemo(
    () => calculateOffersSummary(buyOffers),
    [buyOffers]
  );

  const activeOperationsCount = useMemo(
    () => beneficiaryOperations.filter((op) => !op.isApproved).length,
    [beneficiaryOperations]
  );

  const totalOperationsCount = useMemo(
    () => beneficiaryOperations.length,
    [beneficiaryOperations]
  );

  const sortOffers = useCallback(
    (offers: IncentiveOffer[], sortBy: IncentiveOfferSortOption): IncentiveOffer[] =>
      [...offers].sort((a, b) => {
        switch (sortBy) {
          case 'price':
            return b.unitPrice - a.unitPrice;
          case 'date':
            return (
              new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
            );
          default:
            return 0;
        }
      }),
    []
  );

  const sortedSellOffers = useMemo(
    () => sortOffers(sellOffers, sellOffersSortBy),
    [sellOffers, sellOffersSortBy, sortOffers]
  );

  const sortedBuyOffers = useMemo(
    () => sortOffers(buyOffers, buyOffersSortBy),
    [buyOffers, buyOffersSortBy, sortOffers]
  );

  const handleCreateSellOffer = useCallback(() => {
    history.push(`/crear-oferta-venta-incentivos/${societyId}${beneficiaryQueryParam}`);
  }, [history, societyId, beneficiaryQueryParam]);

  const handleCreateBuyOffer = useCallback(() => {
    history.push(`/crear-oferta-compra-incentivos/${societyId}${beneficiaryQueryParam}`);
  }, [history, societyId, beneficiaryQueryParam]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta-incentivos/${societyId}/${offerId}${beneficiaryQueryParam}`);
    },
    [history, societyId, beneficiaryQueryParam]
  );

  const handleSellOffersSortChange = useCallback(
    (sortBy: IncentiveOfferSortOption) => {
      setSellOffersSortBy(sortBy);
    },
    []
  );

  const handleBuyOffersSortChange = useCallback(
    (sortBy: IncentiveOfferSortOption) => {
      setBuyOffersSortBy(sortBy);
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        sellOffers: sortedSellOffers,
        buyOffers: sortedBuyOffers,
        beneficiaryOperations,
        sellOffersSummary,
        buyOffersSummary,
        activeOperationsCount,
        totalOperationsCount,
        sellOffersSortBy,
        buyOffersSortBy,
        isAdmin,
        isReadOnly,
        isLoading: isIncentiveOffersLoading || !actualSociety,
      },
      actions: {
        handleCreateSellOffer,
        handleCreateBuyOffer,
        handleViewOfferDetail,
        handleSellOffersSortChange,
        handleBuyOffersSortChange,
      },
    }),
    [
      societyId,
      actualSociety,
      sortedSellOffers,
      sortedBuyOffers,
      beneficiaryOperations,
      sellOffersSummary,
      buyOffersSummary,
      activeOperationsCount,
      totalOperationsCount,
      sellOffersSortBy,
      buyOffersSortBy,
      isAdmin,
      isReadOnly,
      isIncentiveOffersLoading,
      handleCreateSellOffer,
      handleCreateBuyOffer,
      handleViewOfferDetail,
      handleSellOffersSortChange,
      handleBuyOffersSortChange,
    ]
  );

  return (
    <IncentiveOffersListContext.Provider value={contextValue}>
      {children}
    </IncentiveOffersListContext.Provider>
  );
};

export const useIncentiveOffersList = () => {
  const context = useContext(IncentiveOffersListContext);
  if (!context) {
    throw new Error(
      'useIncentiveOffersList must be used within a IncentiveOffersListProvider'
    );
  }
  return context;
};
